import React, { FunctionComponent } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Callout } from '~/components/ui';
import type { AuthErrorKind } from './authErrors';
import styles from './authForm.module.scss';

/** Rodzaje błędu pokazywane w komunikacie nad formularzem (reszta stoi przy polach). */
type CalloutKind = Extract<
  AuthErrorKind,
  'credentials' | 'tooMany' | 'disabled' | 'network' | 'unknown'
>;

/** Dalsza droga z komunikatu; wpisany e-mail przechodzi dalej, żeby nie wpisywać go drugi raz. */
const LINKS: Partial<Record<CalloutKind, (email?: string) => string>> = {
  credentials: (email) =>
    `/auth/login-by-link${email ? `?email=${encodeURIComponent(email)}` : ''}`,
  tooMany: (email) => `/auth/forgot-password${email ? `/${encodeURIComponent(email)}` : ''}`,
};

export function isCalloutKind(kind: AuthErrorKind | null): kind is CalloutKind {
  return (
    kind === 'credentials' ||
    kind === 'tooMany' ||
    kind === 'disabled' ||
    kind === 'network' ||
    kind === 'unknown'
  );
}

/** Błąd z Firebase nad formularzem: mówi, co się stało i co zrobić; dostaje fokus. */
interface AuthErrorCalloutProps {
  kind: CalloutKind;
  email?: string;
  /** Rejestracja: „za dużo prób” bez mowy o logowaniu i bez linku do zmiany hasła. */
  context?: 'login' | 'register';
}

const AuthErrorCallout: FunctionComponent<AuthErrorCalloutProps> = ({
  kind,
  email,
  context = 'login',
}) => {
  const { t } = useTranslation('auth');
  const key = context === 'register' && kind === 'tooMany' ? 'tooManyRegister' : kind;
  const target = context === 'login' ? LINKS[kind]?.(email) : undefined;

  return (
    <div className={styles.callout}>
      <Callout tone="error" title={t(`auth:screens.errors.${key}.title`)} role="alert" focusOnMount>
        <Trans
          t={t}
          i18nKey={`auth:screens.errors.${key}.text`}
          components={{ go: target ? <Link to={target} /> : <span /> }}
        />
      </Callout>
    </div>
  );
};

export default AuthErrorCallout;
